import React from 'react';
import axios from 'axios';
import {
  ContentState,
  Editor,
  EditorState,
  RichUtils,
  convertFromHTML,
  getDefaultKeyBinding
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'draft-js/dist/Draft.css';

const contentApiBaseUrl = 'https://lrfiosxbs6.execute-api.ap-northeast-2.amazonaws.com';

const inlineControls = [
  {
    label: <span className="admin-editor-label admin-editor-label-bold">B</span>,
    ariaLabel: 'Bold',
    style: 'BOLD'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-italic">I</span>,
    ariaLabel: 'Italic',
    style: 'ITALIC'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-underline">U</span>,
    ariaLabel: 'Underline',
    style: 'UNDERLINE'
  }
];

const blockControls = [
  {
    label: <span className="admin-editor-label admin-editor-label-h1">H1</span>,
    ariaLabel: 'Heading 1',
    style: 'header-one'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-h2">H2</span>,
    ariaLabel: 'Heading 2',
    style: 'header-two'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-quote">"</span>,
    ariaLabel: 'Quote',
    style: 'blockquote'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-list">• List</span>,
    ariaLabel: 'Bulleted list',
    style: 'unordered-list-item'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-list">1. List</span>,
    ariaLabel: 'Numbered list',
    style: 'ordered-list-item'
  }
];

function EditorToolbarButton({ active, ariaLabel, label, onMouseDown }) {
  return (
    <button
      type="button"
      className={`admin-editor-button${active ? ' active' : ''}`}
      onMouseDown={onMouseDown}
      aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
    >
      {label}
    </button>
  );
}

class AdminContentManager extends React.Component {
  state = {
    editorState: EditorState.createEmpty(),
    toastMessage: '',
    isSaving: false
  }

  componentDidMount() {
    this.fetchContent();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.contentType !== this.props.contentType) {
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
      }

      this.setState({
        toastMessage: '',
        isSaving: false
      });
      this.fetchContent();
    }
  }

  componentWillUnmount() {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  fetchContent = () => {
    axios.get(`${contentApiBaseUrl}/default/content`, {
      params: {
        content_type: this.props.contentType
      }
    })
      .then((response) => {
        console.log(`${this.props.title} content response`, response.data);

        const contentText = this.extractContentText(response.data);

        if (contentText) {
          const blocksFromHTML = convertFromHTML(contentText);

          this.setState({
            editorState: blocksFromHTML.contentBlocks && blocksFromHTML.contentBlocks.length > 0
              ? EditorState.createWithContent(
                  ContentState.createFromBlockArray(
                    blocksFromHTML.contentBlocks,
                    blocksFromHTML.entityMap
                  )
                )
              : EditorState.createWithContent(ContentState.createFromText(contentText))
          });
        }
      })
      .catch((error) => {
        console.error(`${this.props.title} content request failed`, error);
      });
  }

  extractContentText = (data) => {
    if (typeof data === 'string') {
      return data;
    }

    if (data && typeof data.content === 'string') {
      return data.content;
    }

    if (data && typeof data.results === 'string') {
      return data.results;
    }

    if (data && Array.isArray(data.results) && data.results.length > 0) {
      const firstResult = data.results[0];

      if (typeof firstResult === 'string') {
        return firstResult;
      }

      if (firstResult && typeof firstResult.content === 'string') {
        return firstResult.content;
      }
    }

    return '';
  }

  handleChange = (editorState) => {
    this.setState({ editorState });
  }

  handleKeyCommand = (command, editorState) => {
    const nextState = RichUtils.handleKeyCommand(editorState, command);

    if (nextState) {
      this.handleChange(nextState);
      return 'handled';
    }

    return 'not-handled';
  }

  mapKeyToEditorCommand = (e) => {
    if (e.key === 'Tab') {
      return null;
    }

    return getDefaultKeyBinding(e);
  }

  toggleInlineStyle = (style) => (e) => {
    e.preventDefault();
    this.handleChange(RichUtils.toggleInlineStyle(this.state.editorState, style));
  }

  toggleBlockType = (style) => (e) => {
    e.preventDefault();
    this.handleChange(RichUtils.toggleBlockType(this.state.editorState, style));
  }

  focusEditor = () => {
    if (this.editorRef) {
      this.editorRef.focus();
    }
  }

  handleSave = () => {
    const html = stateToHTML(this.state.editorState.getCurrentContent());
    const token = localStorage.getItem('access_token');

    const payload = {
      content_type: this.props.contentType,
      content: html
    };

    console.log(`${this.props.title} content save payload`, payload);

    this.setState({ isSaving: true });

    axios.put(`${contentApiBaseUrl}/default/content`, payload, {
      headers: {
        token
      }
    })
      .then((response) => {
        console.log(`${this.props.title} content save response`, response.data);
        this.setState({
          toastMessage: '수정이 완료되었습니다.',
          isSaving: false
        });

        this.toastTimer = setTimeout(() => {
          this.setState({ toastMessage: '' });
        }, 2500);
      })
      .catch((error) => {
        console.error(`${this.props.title} content save request failed`, error);
        this.setState({ isSaving: false });
      });
  }

  render() {
    const currentInlineStyle = this.state.editorState.getCurrentInlineStyle();
    const currentBlockType = this.state.editorState
      .getCurrentContent()
      .getBlockForKey(this.state.editorState.getSelection().getStartKey())
      .getType();

    return (
      <div className="admin-panel-card">
        <div className="admin-panel-eyebrow">Editor</div>
        <h2 className="admin-panel-title">{this.props.title}</h2>
        <p className="admin-panel-description">{this.props.description}</p>
        {this.state.toastMessage ? (
          <div className="admin-success-toast">{this.state.toastMessage}</div>
        ) : null}

        <div className="admin-editor-shell">
          <div className="admin-editor-toolbar">
            {inlineControls.map((control) => (
              <EditorToolbarButton
                key={control.style}
                active={currentInlineStyle.has(control.style)}
                ariaLabel={control.ariaLabel}
                label={control.label}
                onMouseDown={this.toggleInlineStyle(control.style)}
              />
            ))}
            {blockControls.map((control) => (
              <EditorToolbarButton
                key={control.style}
                active={currentBlockType === control.style}
                ariaLabel={control.ariaLabel}
                label={control.label}
                onMouseDown={this.toggleBlockType(control.style)}
              />
            ))}
          </div>

          <div className="admin-editor-surface" onClick={this.focusEditor}>
            <Editor
              editorState={this.state.editorState}
              onChange={this.handleChange}
              handleKeyCommand={this.handleKeyCommand}
              keyBindingFn={this.mapKeyToEditorCommand}
              placeholder={`${this.props.title} 내용을 입력하세요.`}
              ref={(element) => {
                this.editorRef = element;
              }}
            />
          </div>

          <div className="admin-editor-actions">
            <button
              type="button"
              className="admin-editor-save"
              onClick={this.handleSave}
              disabled={this.state.isSaving}
            >
              {this.state.isSaving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AdminContentManager;
